/**
 * Pages/Panel/PreNomina/SobreRecibo/TablaConceptosRecibo.js
 *
 * La tabla de conceptos de un recibo: percepciones y deducciones lado a lado, con el importe
 * editable, la marca del valor forzado, la fila para agregar un concepto y el pie de totales.
 *
 * Vive aquí y no dentro de `SobreRecibo.js` porque la comparten DOS pantallas que hacen lo mismo
 * —revisar y ajustar un recibo— y que tienen que verse iguales: el sobre-recibo de la prenómina y
 * el desglose del finiquito. Mismo motivo por el que ya se había sacado `CeldaEditable`. Si cada
 * una tuviera su copia, la siguiente corrección se aplicaría a una y no a la otra.
 *
 * Lo que cambia entre pantallas viaja por props, no por ramas internas:
 *  · qué renglones se pueden quitar (`puedeQuitar`) y qué pasa al quitarlos (`onQuitar`);
 *  · si el nombre abre la memoria de cálculo al doble clic (`onDetalleConcepto`, sólo prenómina);
 *  · las etiquetas extra del nombre (`extrasNombre`, p. ej. el tag «CFDI sep.» del finiquito).
 */

import React, { useState } from "react";
import { InputNumber, Popconfirm, Tag, Tooltip } from "antd";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import CeldaEditable from "./CeldaEditable";
import SelectConceptosNominaItem from "../../../../Components/SelectConceptosNominaItem";

// ─── Estilos ─────────────────────────────────────────────────────────────────
// Exportados: son los del recibo y los usan las dos pantallas para lo que rodea a la tabla.
export const TH = {
  border: "1px solid #d0d0d0",
  background: "#f5f5f5",
  padding: "5px 8px",
  fontSize: 11,
  fontWeight: 600,
  color: "#555",
  whiteSpace: "nowrap",
  textAlign: "center",
};
export const TD = { border: "1px solid #d0d0d0", padding: "4px 8px", fontSize: 12, verticalAlign: "middle" };
export const TD_NUM = { ...TD, textAlign: "right", fontFamily: "monospace" };
export const TD_SEP = { border: "none", width: 16, background: "#fafafa" };

export const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const esManual = (v) => v !== null && v !== undefined;
const importeEfectivo = (c) =>
  (esManual(c?.importeTotalManual) ? c.importeTotalManual : c?.importeTotal) || 0;

const idDe = (c) => (c?.conceptoNominaId == null ? "" : String(c.conceptoNominaId));

// Ámbar para las percepciones en especie. Es el aviso visual de por qué el renglón se ve en
// la columna pero no está dentro de TOTAL PERCEPCIONES ni del NETO. Deliberadamente distinto
// del gris de los informativos: la especie sí se timbra y sí grava.
const ESTILO_ESPECIE = { color: "#AD6800", background: "#fffbe6" };

// ─── Celda del nombre, común a percepciones y deducciones ────────────────────
const CeldaNombre = ({
  concepto: c, noSuma, especie, soloLectura, onDetalleConcepto, onLimpiarManual,
  puedeQuitar, onQuitar, tituloQuitar, extrasNombre,
}) => {
  const id = idDe(c);
  const hayManual = esManual(c.valorManual) || esManual(c.importeTotalManual);
  const tdGris = noSuma ? { color: "#bbb" } : especie ? ESTILO_ESPECIE : {};
  const tipoLabel = c.tipo === 3 ? "OBL" : null;

  return (
    <td
      style={{ ...TD, ...tdGris, cursor: onDetalleConcepto ? "pointer" : "default" }}
      onDoubleClick={onDetalleConcepto ? () => onDetalleConcepto(id) : undefined}
    >
      <Tooltip
        title={onDetalleConcepto ? "Doble clic para ver cómo se calculó" : undefined}
        mouseEnterDelay={0.6}
      >
        <span>
          <span style={{ fontFamily: "monospace", fontSize: 10, color: "#ccc", marginRight: 5 }}>
            {c.serie}-{c.numero}
          </span>
          {c.descripcion}
        </span>
      </Tooltip>

      {tipoLabel && (
        <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>{tipoLabel}</Tag>
      )}
      {noSuma && (
        <Tag color="default" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px", color: "#bbb" }}>
          Info
        </Tag>
      )}
      {/* Distinto de "Info": la percepción en especie SÍ se timbra y SÍ grava. Lo único que
          no hace es entrar al neto, porque no se le entrega en dinero al empleado. */}
      {especie && (
        <Tooltip title="Se entrega en especie: se timbra y grava como cualquier percepción, pero no suma al neto a pagar ni sale en la dispersión.">
          <Tag color="orange" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>
            Especie
          </Tag>
        </Tooltip>
      )}
      {extrasNombre ? extrasNombre(c) : null}

      {/* Los controles de la celda paran la propagación: dos clics rápidos sobre el tag o el
          bote de basura son eso, no una petición de desglose. */}
      {hayManual && (
        soloLectura || !onLimpiarManual ? (
          <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, padding: "0 4px" }}>M</Tag>
        ) : (
          <Popconfirm
            // Con días capturados, quitar el manual recalcula la línea (el backend en la prenómina,
            // el motor en el finiquito); un importe manual solo queda en 0 hasta recalcular.
            title={esManual(c.valorManual)
              ? "¿Quitar los días capturados de este concepto? La línea se recalculará con los días del periodo."
              : "¿Quitar el valor manual de este concepto? Quedará en 0 hasta que recalcules la línea."}
            okText="Quitar"
            cancelText="Cancelar"
            onConfirm={() => onLimpiarManual(id)}
          >
            <Tag
              color="blue"
              style={{ marginLeft: 4, fontSize: 10, padding: "0 4px", cursor: "pointer" }}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              M ✕
            </Tag>
          </Popconfirm>
        )
      )}

      {!soloLectura && puedeQuitar && puedeQuitar(c) && (
        <Popconfirm
          title={tituloQuitar ? tituloQuitar(c) : "¿Quitar este concepto?"}
          okText="Quitar"
          cancelText="Cancelar"
          onConfirm={() => onQuitar(c)}
        >
          <DeleteOutlined
            style={{ marginLeft: 6, color: "#ff4d4f", cursor: "pointer" }}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      )}
    </td>
  );
};

// ─── Fila de alta ────────────────────────────────────────────────────────────
// Una por columna, con su propio borrador. El `tipo` lo fija la columna, así que no hace falta un
// selector de percepción/deducción como el de la pestaña 1 del finiquito.
const FilaAgregar = ({ tipo, empresaId, headersToken, excluidos, onAgregar, conValor }) => {
  const [nuevo, setNuevo] = useState({ conceptoId: null, concepto: null, importe: null });
  const [guardando, setGuardando] = useState(false);

  const confirmar = async () => {
    const importeNum = parseFloat(nuevo.importe);
    if (!nuevo.conceptoId || isNaN(importeNum)) return;
    setGuardando(true);
    const ok = await onAgregar(nuevo.conceptoId, importeNum, nuevo.concepto);
    setGuardando(false);
    // Sólo se limpia el borrador si de verdad se agregó: con un rechazo (duplicado, concepto
    // inválido) el usuario tiene que poder ver y corregir lo que capturó.
    if (ok !== false) setNuevo({ conceptoId: null, concepto: null, importe: null });
  };

  return (
    <>
      <td style={{ ...TD, padding: "3px 4px" }}>
        <SelectConceptosNominaItem
          tipo={tipo}
          empresaId={empresaId}
          headersToken={headersToken}
          excludeIds={excluidos}
          value={nuevo.conceptoId}
          onChange={(id, concepto) => setNuevo({ ...nuevo, conceptoId: id, concepto })}
          disabled={guardando}
          placeholder={tipo === 2 ? "+ Agregar deducción..." : "+ Agregar percepción..."}
          width="100%"
        />
      </td>
      {conValor && <td style={{ ...TD_NUM, width: 70 }} />}
      <td style={{ ...TD_NUM, width: 90, padding: "3px 4px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
          <InputNumber
            size="small"
            style={{ width: 70 }}
            min={0}
            precision={2}
            placeholder="$0.00"
            value={nuevo.importe}
            disabled={guardando}
            onChange={(v) => setNuevo({ ...nuevo, importe: v })}
            onPressEnter={confirmar}
          />
          <Tooltip title="Agregar el concepto al recibo">
            <PlusCircleOutlined
              style={{ color: "#1677ff", cursor: "pointer", fontSize: 15 }}
              onClick={confirmar}
            />
          </Tooltip>
        </div>
      </td>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const TablaConceptosRecibo = ({
  percepciones = [],
  deducciones = [],
  noSumaIds = new Set(),
  // Percepciones en especie: se muestran con su importe y marcadas, pero fuera del neto.
  especieIds = new Set(),
  soloLectura = false,
  empresaId,
  headersToken,
  onEditarImporte,
  onLimpiarManual,
  // (conceptoNominaId, importe, conceptoDelCatalogo) => boolean|Promise<boolean>. Sin él no se
  // pinta la fila de alta.
  onAgregar,
  puedeQuitar,
  onQuitar,
  tituloQuitar,
  onDetalleConcepto,
  extrasNombre,
  // Ids que el selector de alta no debe ofrecer. La regla es **lo que esta pantalla ya deja
  // editar**, no "todo lo que trae la línea": si una pantalla oculta los renglones en cero y
  // además los excluye, esos conceptos quedan inalcanzables —ni se ven para editarles el
  // importe, ni se pueden agregar—. Por omisión se arma con los conceptos que se están
  // pintando, que es justo esa regla; el sobre-recibo los muestra todos y no lo pasa, y el
  // desglose del finiquito, que sí esconde los ceros, lo arma aparte (conceptosDelRecibo.js).
  excluidos,
  mensajeVacio = "Sin conceptos",
  nota,
}) => {
  // El catálogo manda, pero se cae al flag que la línea trae en su snapshot: un concepto
  // agregado a mano puede no estar en el catálogo cargado, y si el catálogo llega sin la
  // bandera el concepto volvería a sumar en silencio. Misma convención que `noSuma`.
  const esInfo = (c) => noSumaIds.has(idDe(c)) || !!c.noSuma;
  const esEspecie = (c) => !esInfo(c) && (especieIds.has(idDe(c)) || !!c.especie);

  // TOTAL PERCEPCIONES son las percepciones EN EFECTIVO: las de especie se totalizan aparte
  // y no llegan al neto, porque no se le entregan en dinero al empleado. El CFDI sí las suma
  // en su TotalPercepciones, así que recibo y comprobante difieren por totalEspecie.
  const totalPerc = percepciones
    .filter((c) => !esInfo(c) && !esEspecie(c))
    .reduce((s, c) => s + importeEfectivo(c), 0);
  const totalEspecie = percepciones
    .filter((c) => esEspecie(c))
    .reduce((s, c) => s + importeEfectivo(c), 0);
  const totalDedu = deducciones
    .filter((c) => !esInfo(c))
    .reduce((s, c) => s + importeEfectivo(c), 0);
  const neto = totalPerc - totalDedu;

  const maxFilas = Math.max(percepciones.length, deducciones.length);
  const filas = Array.from({ length: maxFilas }, (_, i) => ({
    perc: percepciones[i] || null,
    dedu: deducciones[i] || null,
  }));

  const idsExcluidos = excluidos
    || new Set([...percepciones, ...deducciones].map(idDe).filter(Boolean));

  const comunes = { soloLectura, onDetalleConcepto, onLimpiarManual, puedeQuitar, onQuitar, tituloQuitar, extrasNombre };

  const celdasVacias = (conValor) => (
    <>
      <td style={{ ...TD, color: "#e0e0e0" }}>—</td>
      {conValor && <td style={{ ...TD_NUM, width: 70 }} />}
      <td style={{ ...TD_NUM, width: 90 }} />
    </>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "auto" }}>
        <thead>
          <tr>
            <th style={{ ...TH, background: "#f6ffed" }} colSpan={3}>PERCEPCIONES</th>
            <th style={{ border: "none", width: 16 }} />
            <th style={{ ...TH, background: "#fff1f0" }} colSpan={2}>DEDUCCIONES</th>
          </tr>
          <tr>
            <th style={{ ...TH, textAlign: "left", minWidth: 180 }}>Concepto</th>
            <th style={{ ...TH, width: 70 }}>Valor</th>
            <th style={{ ...TH, width: 90 }}>Importe</th>
            <th style={TD_SEP} />
            <th style={{ ...TH, textAlign: "left", minWidth: 200 }}>Concepto</th>
            <th style={{ ...TH, width: 90 }}>Importe</th>
          </tr>
        </thead>

        <tbody>
          {filas.map((fila, i) => (
            <tr key={i}>
              {fila.perc ? (
                <>
                  <CeldaNombre
                    {...comunes}
                    concepto={fila.perc}
                    noSuma={esInfo(fila.perc)}
                    especie={esEspecie(fila.perc)}
                  />
                  {/* El Valor (la cantidad) sólo es editable en percepciones: es la regla del
                      motor, y los endpoints de importes rechazan `valorManual` en deducciones. */}
                  <td style={{ ...TD_NUM, width: 70, ...(esInfo(fila.perc) ? { color: "#bbb" } : esEspecie(fila.perc) ? ESTILO_ESPECIE : {}) }}>
                    <CeldaEditable
                      key={`v-${idDe(fila.perc)}-${esManual(fila.perc.valorManual) ? fila.perc.valorManual : (fila.perc.valor || 0)}`}
                      valor={esManual(fila.perc.valorManual) ? fila.perc.valorManual : (fila.perc.valor || 0)}
                      esManual={esManual(fila.perc.valorManual)}
                      ancho={60}
                      disabled={soloLectura}
                      onGuardar={(nv) => onEditarImporte(idDe(fila.perc), "valorManual", nv)}
                    />
                  </td>
                  <td style={{ ...TD_NUM, width: 90, ...(esInfo(fila.perc) ? { color: "#bbb" } : esEspecie(fila.perc) ? ESTILO_ESPECIE : {}) }}>
                    <CeldaEditable
                      key={`i-${idDe(fila.perc)}-${importeEfectivo(fila.perc)}`}
                      valor={importeEfectivo(fila.perc)}
                      esManual={esManual(fila.perc.importeTotalManual)}
                      ancho={80}
                      disabled={soloLectura}
                      onGuardar={(nv) => onEditarImporte(idDe(fila.perc), "importeTotalManual", nv)}
                    />
                  </td>
                </>
              ) : celdasVacias(true)}

              <td style={TD_SEP} />

              {fila.dedu ? (
                <>
                  <CeldaNombre
                    {...comunes}
                    concepto={fila.dedu}
                    noSuma={esInfo(fila.dedu)}
                  />
                  <td style={{ ...TD_NUM, width: 90, ...(esInfo(fila.dedu) ? { color: "#bbb" } : {}) }}>
                    <CeldaEditable
                      key={`i-${idDe(fila.dedu)}-${importeEfectivo(fila.dedu)}`}
                      valor={importeEfectivo(fila.dedu)}
                      esManual={esManual(fila.dedu.importeTotalManual)}
                      ancho={80}
                      disabled={soloLectura}
                      onGuardar={(nv) => onEditarImporte(idDe(fila.dedu), "importeTotalManual", nv)}
                    />
                  </td>
                </>
              ) : celdasVacias(false)}
            </tr>
          ))}

          {filas.length === 0 && (
            <tr>
              <td colSpan={6} style={{ ...TD, textAlign: "center", color: "#aaa", padding: 24 }}>
                {mensajeVacio}
              </td>
            </tr>
          )}

          {!soloLectura && onAgregar && (
            <tr style={{ background: "#fafafa" }}>
              <FilaAgregar
                tipo={1}
                conValor
                empresaId={empresaId}
                headersToken={headersToken}
                excluidos={idsExcluidos}
                onAgregar={onAgregar}
              />
              <td style={TD_SEP} />
              <FilaAgregar
                tipo={2}
                empresaId={empresaId}
                headersToken={headersToken}
                excluidos={idsExcluidos}
                onAgregar={onAgregar}
              />
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr style={{ background: "#f0f0f0", fontWeight: 700 }}>
            <td style={{ ...TD, fontWeight: 700 }}>TOTAL PERCEPCIONES</td>
            <td style={TD_NUM} />
            <td style={{ ...TD_NUM, color: "#3B6D11", background: "#f6ffed" }}>{fmt(totalPerc)}</td>
            <td style={TD_SEP} />
            <td style={{ ...TD, fontWeight: 700 }}>TOTAL DEDUCCIONES</td>
            <td style={{ ...TD_NUM, color: "#A32D2D", background: "#fff1f0" }}>{fmt(totalDedu)}</td>
          </tr>
          {/* Sólo aparece si hay algo en especie: en la nómina normal no estorba. Va entre
              los totales y el neto porque explica por qué el neto no es percepciones menos
              deducciones a secas. */}
          {totalEspecie !== 0 && (
            <tr style={{ background: "#fff7e6", fontWeight: 700 }}>
              <td style={{ ...TD, fontWeight: 700 }}>TOTAL EN ESPECIE</td>
              <td style={TD_NUM} />
              <td style={{ ...TD_NUM, color: "#AD6800" }}>{fmt(totalEspecie)}</td>
              <td style={TD_SEP} />
              <td colSpan={2} style={{ ...TD, fontWeight: 400, fontSize: 10, color: "#AD6800" }}>
                Se timbra y grava, pero no se paga en efectivo: fuera del neto.
              </td>
            </tr>
          )}
          <tr style={{ background: "#fafafa" }}>
            <td colSpan={6} style={{ ...TD, textAlign: "right", fontWeight: 700, fontSize: 14 }}>
              NETO A PAGAR:&nbsp;
              <span style={{ color: "#222", fontSize: 16 }}>${fmt(neto)}</span>
            </td>
          </tr>
        </tfoot>
      </table>

      {nota && <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>{nota}</div>}
    </div>
  );
};

export default TablaConceptosRecibo;
